// Comportements propres à l'accueil (maquette « 30 sept ») : rail des entreprises piloté par le
// défilement (repli : rail à glisser), fermes en accordéon, étape active des « six maillons »
// (bloc en attente), épingles de la carte des implantations. La molette n'est jamais retenue
// (doctrine docs/06) : le rail est épinglé en CSS et suit la position de la page.

import {
  closeDialog,
  openDialog,
  swipeToClose,
  trapFocus
} from './dialogue-anima'

const DRAG_THRESHOLD = 5 // px avant de considérer qu'on tire le rail
const SCRUB_QUERY = '(min-width: 992px) and (prefers-reduced-motion: no-preference)'
const SCRUB_START = 0.04 // part de course avant le départ du rail (le bloc se pose)
const SCRUB_SPAN = 0.9 // part de course consacrée au rail (le reste : arrêt sur la dernière carte)
const ENTRY_RISE = 90 // px de montée du bloc à son arrivée
const ENTRY_SCALE = 0.03 // 97 % → 100 %
const ENTRY_POWER = 3 // sortie cubique
const SCALE_DIGITS = 4 // décimales de l'échelle écrite dans le style
const MOBILE_ACTIVE_DELAY = 2000 // ms avant qu'une carte bien visible s'ouvre seule (mobile)
const MOBILE_ACTIVE_RATIO = 0.6
const MODAL_FOCUS_DELAY = 60 // ms, laisse la fenêtre s'afficher avant de déplacer le focus

const clamp01 = (v) => Math.min(1, Math.max(0, v))

// Rail des entreprises : glisser à la souris (le tactile et la molette défilent nativement).
const initRail = (rail) => {
  let startX = 0
  let startScroll = 0
  let dragging = false
  let moved = false

  rail.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || rail.closest('.is-scrub')) {
      return
    }
    dragging = true
    moved = false
    startX = e.clientX
    startScroll = rail.scrollLeft
  })
  rail.addEventListener('pointermove', (e) => {
    if (!dragging) {
      return
    }
    const dx = e.clientX - startX
    if (Math.abs(dx) > DRAG_THRESHOLD) {
      moved = true
      rail.classList.add('is-dragging')
    }
    if (moved) {
      rail.scrollLeft = startScroll - dx
    }
  })
  const stop = () => {
    dragging = false
    rail.classList.remove('is-dragging')
  }
  rail.addEventListener('pointerup', stop)
  rail.addEventListener('pointerleave', stop)
  // Un glisser ne doit pas ouvrir la carte sous le pointeur.
  rail.addEventListener('click', (e) => {
    if (moved) {
      e.preventDefault()
      moved = false
    }
  }, true)
  rail.addEventListener('dragstart', (e) => e.preventDefault())
}

// Rail au défilement : la section est rallongée (CSS), son contenu collant ; la piste glisse de
// p × maxT, p étant la progression dans la section, avec un léger lissage. Le rAF ne tourne que
// le temps de rejoindre la cible ; l'écoute du défilement n'existe que section à l'écran.
const initRailScrub = (pin) => {
  const mq = window.matchMedia(SCRUB_QUERY)
  const entry = pin.querySelector('[data-anima-ents-entry]')
  const rail = pin.querySelector('[data-anima-rail]')
  if (!entry || !rail) {
    return
  }
  // Hauteur de l'en-tête publiée en style en ligne par entete-anima.js : lue sans recalcul.
  const headH = () => parseFloat(document.documentElement.style.getPropertyValue('--anima-header-h')) || 0
  let range = 0
  let maxT = 0
  let raf = 0
  let on = false

  const measure = () => {
    const cards = rail.querySelectorAll('.anima-cocard')
    if (!cards.length) {
      return
    }
    const first = cards[0]
    const last = cards[cards.length - 1]
    // Bord gauche de la piste hors transformations (le bloc d'entrée est mis à l'échelle).
    const base = entry.parentElement.getBoundingClientRect().left + entry.offsetLeft + rail.offsetLeft
    maxT = Math.max(0, base + last.offsetLeft + last.offsetWidth - document.documentElement.clientWidth)
    range = pin.offsetHeight - (window.innerHeight - headH())
    rail.style.setProperty('--anima-ents-voile', `${last.offsetLeft + last.offsetWidth - first.offsetLeft}px`)
  }
  // Suivi direct du défilement (retour d'Antoine du 01/10 : le lissage donnait un retard
  // d'environ 115 ms, « il faut forcer sur la molette »). Une lecture, puis les écritures, une
  // fois par image.
  const render = () => {
    raf = 0
    const h = headH()
    const top = pin.getBoundingClientRect().top
    const x = range > 0 ? clamp01(((h - top) / range - SCRUB_START) / SCRUB_SPAN) * maxT : 0
    const e = 1 - (1 - clamp01((window.innerHeight - top) / (window.innerHeight - h))) ** ENTRY_POWER
    rail.style.transform = `translate3d(${(-x).toFixed(1)}px, 0, 0)`
    entry.style.transform = `translate3d(0, ${((1 - e) * ENTRY_RISE).toFixed(1)}px, 0) scale(${(1 - ENTRY_SCALE * (1 - e)).toFixed(SCALE_DIGITS)})`
  }
  const update = () => {
    if (!raf) {
      raf = requestAnimationFrame(render)
    }
  }
  const io = new IntersectionObserver(([en]) => {
    if (en.isIntersecting) {
      window.addEventListener('scroll', update, {
        passive: true
      })
      update()
    } else {
      window.removeEventListener('scroll', update)
    }
  })
  const ro = new ResizeObserver(() => {
    if (on) {
      measure()
      update()
    }
  })
  const enable = () => {
    on = true
    pin.classList.add('is-scrub')
    rail.scrollLeft = 0
    measure()
    io.observe(pin)
    ro.observe(pin)
    update()
  }
  const disable = () => {
    on = false
    pin.classList.remove('is-scrub')
    io.disconnect()
    ro.disconnect()
    window.removeEventListener('scroll', update)
    cancelAnimationFrame(raf)
    raf = 0
    rail.style.transform = ''
    entry.style.transform = ''
  }
  // Clavier : une carte qui prend le focus hors champ ramène la page à la position qui la montre.
  rail.addEventListener('focusin', (ev) => {
    const card = ev.target.closest('.anima-cocard')
    const first = rail.querySelector('.anima-cocard')
    if (!on || !card || !maxT || !first) {
      return
    }
    const p = clamp01((card.offsetLeft - first.offsetLeft) / maxT)
    const y = window.scrollY + pin.getBoundingClientRect().top - headH() + (SCRUB_START + p * SCRUB_SPAN) * range
    window.scrollTo({
      top: y
    })
  })
  mq.addEventListener('change', () => {
    if (mq.matches) {
      enable()
    } else {
      disable()
    }
  })
  if (mq.matches) {
    enable()
  }
}

// Mobile (sous md) : la carte entreprise la plus visible s'ouvre seule après 2 s, comme au
// survol (maquette « 30 sept », `data-mhover`).
const initRailMobile = (rail) => {
  if (!window.matchMedia('(max-width: 767.98px)').matches || !('IntersectionObserver' in window)) {
    return
  }
  const timers = new Map()
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      clearTimeout(timers.get(en.target))
      if (en.intersectionRatio >= MOBILE_ACTIVE_RATIO) {
        timers.set(en.target, setTimeout(() => en.target.classList.add('is-active'), MOBILE_ACTIVE_DELAY))
      } else {
        en.target.classList.remove('is-active')
      }
    })
  }, {
    threshold: [0, MOBILE_ACTIVE_RATIO]
  })
  rail.querySelectorAll('.anima-cocard').forEach((c) => io.observe(c))
}

// Fermes : la vidéo d'un panneau n'est chargée qu'au premier survol (ou à l'activation sur
// mobile), lue tant que le panneau est ouvert, mise en pause ensuite. Sur mobile, le panneau qui
// traverse le centre de l'écran s'ouvre seul.
const initFermes = (root) => {
  const panels = [...root.querySelectorAll('[data-anima-ferme]')]
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const play = (panel) => {
    const video = panel.querySelector('video[data-src]')
    if (!video || reduced) {
      return
    }
    if (!video.src) {
      video.src = video.dataset.src
      video.addEventListener('playing', () => video.classList.add('is-ready'), {
        once: true
      })
    }
    video.play().catch(() => null)
  }
  const stop = (panel) => {
    const video = panel.querySelector('video')
    if (video) {
      video.pause()
    }
  }
  panels.forEach((panel) => {
    panel.addEventListener('mouseenter', () => play(panel))
    panel.addEventListener('mouseleave', () => stop(panel))
  })
  if (window.matchMedia('(max-width: 767.98px)').matches && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        en.target.classList.toggle('is-active', en.isIntersecting)
        if (en.isIntersecting) {
          play(en.target)
        } else {
          stop(en.target)
        }
      })
    }, {
      rootMargin: '-45% 0px -45% 0px'
    })
    panels.forEach((p) => io.observe(p))
  }
}

// Six maillons : l'étape qui traverse le milieu de la fenêtre devient active.
const initMaillons = (list) => {
  const items = [...list.children]
  if (!('IntersectionObserver' in window)) {
    return
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        items.forEach((li) => li.classList.toggle('is-active', li === entry.target))
      }
    })
  }, {
    rootMargin: '-45% 0px -45% 0px'
  })
  items.forEach((li) => io.observe(li))
}

// Implantations : le survol, le focus ou le clic d'une épingle affichent la fiche du site à
// gauche ; le clic ouvre aussi la fenêtre du site (maquette « 30 sept », `[data-modal]`) :
// Échap, voile, Tab cyclique, focus sur le titre, feuille glissable sur mobile.
const initImplantations = (root) => {
  const pins = [...root.querySelectorAll('[data-anima-site]')]
  const cards = [...root.querySelectorAll('[data-anima-site-card]')]
  const select = (id) => {
    pins.forEach((p) => {
      const on = p.dataset.animaSite === id
      p.classList.toggle('is-on', on)
      p.setAttribute('aria-pressed', on ? 'true' : 'false')
    })
    cards.forEach((c) => {
      const on = c.dataset.animaSiteCard === id
      if (on && c.hidden) {
        // La fiche rejoue son entrée en cascade (components/_implantations.scss).
        c.classList.remove('is-anim')
        // Lecture de la mise en page : l'animation repart à chaque changement de site.
        c.getBoundingClientRect()
        c.classList.add('is-anim')
      }
      c.hidden = !on
    })
  }

  // La fenêtre est rattachée au <body> : une section transformée casserait son `position: fixed`.
  const scope = root.closest('section') || document
  const modal = scope.querySelector('[data-anima-site-modal]')
  let opener = null
  const close = () => {
    if (!modal.hidden) {
      closeDialog(modal, () => opener && opener.focus({
        preventScroll: true
      }))
    }
  }
  if (modal) {
    document.body.appendChild(modal)
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-anima-site-close]')) {
        close()
      }
    })
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close()
      }
      trapFocus(modal, e)
    })
    swipeToClose(modal.querySelector('.anima-modale-handle'), modal.querySelector('.anima-modale-box'), close)
  }
  const open = (pin) => {
    const tpl = scope.querySelector(`[data-anima-site-tpl="${pin.dataset.animaSite}"]`)
    if (!modal || !tpl) {
      return
    }
    opener = pin
    const content = modal.querySelector('[data-anima-site-content]')
    content.replaceChildren(tpl.content.cloneNode(true))
    openDialog(modal)
    const title = content.querySelector('#anima-msite-titre')
    setTimeout(() => title && title.focus({
      preventScroll: true
    }), MODAL_FOCUS_DELAY)
  }

  pins.forEach((p) => {
    p.addEventListener('mouseenter', () => select(p.dataset.animaSite))
    p.addEventListener('focus', () => select(p.dataset.animaSite))
    p.addEventListener('click', () => {
      select(p.dataset.animaSite)
      open(p)
    })
  })
}

const init = () => {
  document.querySelectorAll('[data-anima-rail]').forEach((rail) => {
    initRail(rail)
    initRailMobile(rail)
  })
  document.querySelectorAll('[data-anima-ents]').forEach(initRailScrub)
  document.querySelectorAll('[data-anima-fermes]').forEach(initFermes)
  document.querySelectorAll('[data-anima-maillons]').forEach(initMaillons)
  document.querySelectorAll('[data-anima-impl]').forEach(initImplantations)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
